// pages/CreateCampaign/CreateCampaignForm.jsx
import React, { useEffect, useState } from "react";
import MDBox from "../../components/MDBox";
import MDTypography from "../../components/MDTypography";
import MDInput from "../../components/MDInput";
import MDButton from "../../components/MDButton";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import PushPinIcon from "@mui/icons-material/PushPin";
import regionType from "../../json/regionTypes.json";
import { DownloadCities, GetCities, searchTypes } from "../../json/citiesUtil";

const CreateCampaignForm = ({ values, changeValue, onSubmit, defaultCampaign }) => {

  const [searchValue, setSearchValue] = useState("");
  const [searchType, setSearchType] = useState("");
  const [RegionsList, setRegionsList] = useState([]);

  const combinedId = (el) => {
    return `${el?.id}_${el?.state?.id || "NA"}_${el?.country?.id || "NA"}`;
  };

  useEffect(() => {
    setRegionsList(GetCities(searchValue, searchType));
  }, [searchValue, searchType]);

  const getRegionTypeInputs = () => {
    switch (values?.regionType) {
      case "custom":
        return <>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="search-type">Search Type</InputLabel>
              <Select
                labelId="search-type"
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                label="Search Type"
                sx={{
                  height: "45px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.23)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.87)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1976d2",
                    borderWidth: "2px",
                  },
                }}
              >
                {searchTypes?.map((el) => (
                  <MenuItem value={el?.id}>{el?.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>


          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e?.target.value);
              }}
              placeholder={"Search"}
            />
          </Grid>


          <MDBox sx={{ padding: "30px", display: "flex", flexFlow: "row wrap", gap: "10px" }}>
            {values?.region?.map((el) => (
              <MDButton sx={{
                fontWeight: "normal",
              }} onClick={() => {
                changeValue(values?.region?.filter((element) => {
                  return combinedId(element) !== combinedId(el);
                }), "region");
              }} color={"success"} key={combinedId(el)}>
                {el?.name}
                {el?.state?.name ? `, ${el?.state?.name}` : ""}
                {el?.country?.name ? `, ${el?.country?.name}` : ""}
                <PushPinIcon color={"#FFF"} />
              </MDButton>
            ))}

            {RegionsList?.filter((region) => {
              return !values?.region?.map((el) => combinedId(el))?.includes(combinedId(region));
            })?.map((el) => (
              <MDButton sx={{
                fontWeight: "normal",
              }} onClick={() => {
                changeValue([...values?.region, { ...el, type: searchType }], "region");
              }} color={"dark"} key={combinedId(el)}>
                {el?.name}
                {el?.state?.name ? `, ${el?.state?.name}` : ""}
                {el?.country?.name ? `, ${el?.country?.name}` : ""}
                <PushPinIcon color={"#FFF"} />
              </MDButton>
            ))}
          </MDBox>

        </>;
      default:
        return "";
    }
  };

  return (
    <MDBox component="form" role="form" onSubmit={onSubmit} p={3}>
      <MDBox mb={4}>
        <MDTypography variant="h4" fontWeight="medium" color="dark">
          {defaultCampaign ? "Edit Campaign" : "Create New Campaign"}
        </MDTypography>
        <MDTypography variant="body2" color="text" mt={1}>
          {defaultCampaign ? "Update your campaign details" : "Fill in the details to create a new campaign"}
        </MDTypography>
      </MDBox>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <MDInput
            type="text"
            label="Campaign Name"
            fullWidth
            required
            value={values.name}
            onChange={(e) => changeValue(e.target.value, "name")}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              value={values.status}
              onChange={(e) => changeValue(e.target.value, "status")}
              label="Status"
              sx={{
                height: "45px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(0, 0, 0, 0.23)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(0, 0, 0, 0.87)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#1976d2",
                  borderWidth: "2px",
                },
              }}
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="paused">Paused</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <MDInput
            type="text"
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={values.description}
            onChange={(e) => changeValue(e.target.value, "description")}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <MDInput
            type="date"
            label="Start Date"
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            value={values.startDate}
            onChange={(e) => changeValue(e.target.value, "startDate")}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <MDInput
            type="date"
            label="End Date"
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            value={values.endDate}
            onChange={(e) => changeValue(e.target.value, "endDate")}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <MDInput
            type="number"
            label="Budget: $"
            placeholder="Budget"
            fullWidth
            required
            value={values.budget}
            onChange={(e) => changeValue(e.target.value, "budget")}
            inputProps={{ min: 0, step: "0.01" }}
          />
        </Grid>

        {
          regionType && values ?
            <>
              <Grid item xs={24} md={12}>
                <MDTypography variant="body2" color="text" mt={1}>
                  Region selection
                </MDTypography>
              </Grid>
              <Grid item xs={24} md={12}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="regionType">Region type</InputLabel>
                  <Select
                    defaultValue={values.regionType}
                    labelId="regionType"
                    value={values?.regionType}
                    onChange={(e) => {
                      changeValue(e.target.value, "regionType");
                    }}
                    label="Region Type"
                    sx={{
                      height: "45px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0, 0, 0, 0.23)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0, 0, 0, 0.87)",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1976d2",
                        borderWidth: "2px",
                      },
                    }}
                  >
                    {regionType?.map((el) => (
                      <MenuItem value={el?.id}>{el?.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
            : ""
        }

        {/*<MDButton onClick={()=> DownloadCities()} color={'success'}>*/}
        {/*  Download JSON*/}
        {/*</MDButton>*/}

        {getRegionTypeInputs()}

        <Grid item xs={12}>
          <MDBox
            mt={3}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="caption" color="text">
              * Required fields
            </MDTypography>
            <MDButton
              variant="gradient"
              color="info"
              type="submit"
              size="large"
            >
              {defaultCampaign ? "Update Campaign" : "Create Campaign"}
            </MDButton>
          </MDBox>
        </Grid>
      </Grid>
    </MDBox>
  );
};

export default CreateCampaignForm;
